import { Fragment } from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import DownloadIcon from '@mui/icons-material/Download';
import Stack from '@mui/material/Stack';
import { Trans, useTranslation } from 'react-i18next';
import pdfImageUrl from '@repo/assets/images/pdf-image.svg?url';
import { colorPalette } from '@repo/mui-config/color-palette';
import { useIsMobile } from '@repo/mui-config/hooks';
import type { ProviderType, LinkedDocument, LoanId } from '@repo/shared/types';
import { documentType } from '@repo/shared/types';

import { useDocumentsByProvider } from '@/components/loan-process/hooks/use-documents-by-provider';

import { SectionHeader } from './section-header';

interface AgreementAndApprovalsDocumentsProps {
  documents: LinkedDocument[];
  currentProvider: ProviderType;
  loanId: LoanId;
}
const getDocumentTitle = (document: LinkedDocument) => {
  if (document.type === documentType.enum.pragma) {
    return <Trans i18nKey="loanProcess.loanSteps.agreementAndApprovals.documents.documentTitle" />;
  }
  const namePosition = document.url.lastIndexOf('/') + 1;

  return decodeURI(document.url.slice(namePosition));
};

export function Documents({
  documents,
  currentProvider,
  loanId,
}: AgreementAndApprovalsDocumentsProps) {
  const {
    isPragmaDocumentType,
    documentsByProvider,
    handleDownloadDocument,
    isPragmaDocFetchError,
    isDocumentDataFetching,
  } = useDocumentsByProvider();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  return (
    <Box>
      <SectionHeader
        sx={{
          marginBottom: isMobile ? 2 : 3,
          marginLeft: 2,
          marginTop: isMobile ? '15px' : 0,
        }}
      >
        {t('loanProcess.loanSteps.agreementAndApprovals.documents.title')}
      </SectionHeader>
      <Stack gap={1}>
        {documentsByProvider(documents, currentProvider).map((doc) => (
          <Fragment key={`document-${doc.type}`}>
            <Paper
              sx={{
                p: '20px',
                border: isPragmaDocFetchError(doc)
                  ? `1px solid ${colorPalette.error.main}`
                  : 'none',
              }}
            >
              <Stack alignItems="center" direction="row" justifyContent="space-between">
                <Stack alignItems="center" direction="row" gap={4}>
                  <Box component="img" src={pdfImageUrl} alt="pdf image" width={34} />
                  <Typography variant="body2">{getDocumentTitle(doc)}</Typography>
                </Stack>
                {isPragmaDocumentType(doc) ? (
                  <Link
                    color={colorPalette.ink.main}
                    component="button"
                    disabled={isDocumentDataFetching}
                    onClick={() => {
                      handleDownloadDocument(loanId, currentProvider);
                    }}
                  >
                    <DownloadIcon />
                  </Link>
                ) : (
                  <Link color={colorPalette.ink.main} href={doc.url} target="_blank">
                    <DownloadIcon />
                  </Link>
                )}
              </Stack>
            </Paper>
            {isPragmaDocFetchError(doc) ? (
              <Typography color="error" fontSize={12}>
                {t('loanProcess.loanSteps.agreementAndApprovals.documents.error')}
              </Typography>
            ) : null}
          </Fragment>
        ))}
      </Stack>
    </Box>
  );
}
